import { useQuery } from "@tanstack/react-query";
import { useUnsafeEffect } from "@wags/tanstack-start/react";
import type { PostHogInterface } from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { useEffectEvent, useRef } from "react";
import { useAuth, useUser } from "@/lib/core/auth-protocol";
import { authQueryOptions } from "@/lib/core/auth";
import { runWhenPostHogReady } from "@/lib/core/deferred-posthog-client";

const AUTH_CTA_SESSION_KEY = "wags:last-auth-cta";
const AUTH_COMPLETION_SESSION_PREFIX = "wags:auth-completion:";
const SIGNUP_CAPTURED_PREFIX = "wags:signed-up:";
const NEW_USER_WINDOW_MS = 10 * 60 * 1000;
const AUTH_CTA_ATTRIBUTION_WINDOW_MS = 30 * 60 * 1000;

type AuthCtaSurface =
    | "favorite_sheet"
    | "favorite_toast"
    | "header"
    | "profile"
    | "provider_contact";

type AuthCtaPayload = {
    capturedAt: string;
    intent: "sign_in_or_sign_up";
    listingId?: string;
    prompt?: string;
    route: string | null;
    surface: AuthCtaSurface;
};

type AuthCompletionInput = {
    route?: string | null;
    userCreatedAt?: Date | number | string | null;
    userId: string;
};

function getCurrentRoute() {
    return typeof window === "undefined" ? null : window.location.pathname;
}

function readSessionValue(key: string) {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeSessionValue(key: string, value: string) {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {}
}

function readLocalValue(key: string) {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeLocalValue(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value);
    } catch {}
}

function parseTime(value: Date | number | string | null | undefined) {
    if (value instanceof Date) {
        return value.getTime();
    }

    if (typeof value === "number") {
        return value;
    }

    if (typeof value === "string") {
        const timestamp = new Date(value).getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
    }

    return null;
}

function readLastAuthCta() {
    const stored = readSessionValue(AUTH_CTA_SESSION_KEY);
    if (!stored) {
        return null;
    }

    try {
        const payload = JSON.parse(stored) as AuthCtaPayload;
        const capturedAt = parseTime(payload.capturedAt);
        if (
            capturedAt === null ||
            Date.now() - capturedAt > AUTH_CTA_ATTRIBUTION_WINDOW_MS
        ) {
            return null;
        }

        return payload;
    } catch {
        return null;
    }
}

function authCompletionProperties(input: AuthCompletionInput) {
    const lastAuthCta = readLastAuthCta();

    return {
        authCtaClickedAt: lastAuthCta?.capturedAt,
        authCtaListingId: lastAuthCta?.listingId,
        authCtaPrompt: lastAuthCta?.prompt,
        authCtaRoute: lastAuthCta?.route,
        authCtaSurface: lastAuthCta?.surface,
        authUserId: input.userId,
        route: input.route ?? getCurrentRoute(),
    };
}

export function captureAuthCtaClicked(
    posthog: Pick<PostHogInterface, "capture">,
    input: {
        listingId?: string;
        prompt?: string;
        route?: string | null;
        surface: AuthCtaSurface;
    },
) {
    const payload: AuthCtaPayload = {
        capturedAt: new Date().toISOString(),
        intent: "sign_in_or_sign_up",
        listingId: input.listingId,
        prompt: input.prompt,
        route: input.route ?? getCurrentRoute(),
        surface: input.surface,
    };

    posthog.capture("auth_cta_clicked", payload);
    writeSessionValue(AUTH_CTA_SESSION_KEY, JSON.stringify(payload));
}

export function captureAuthCompleted(
    posthog: Pick<PostHogInterface, "capture">,
    input: AuthCompletionInput,
) {
    const createdAt = parseTime(input.userCreatedAt);
    const isNewUser =
        createdAt !== null &&
        Date.now() >= createdAt &&
        Date.now() - createdAt <= NEW_USER_WINDOW_MS;
    const sessionKey = `${AUTH_COMPLETION_SESSION_PREFIX}${input.userId}`;
    const sessionCompletion = readSessionValue(sessionKey);

    if (isNewUser) {
        const signupKey = `${SIGNUP_CAPTURED_PREFIX}${input.userId}`;
        if (
            readLocalValue(signupKey) === "true" ||
            sessionCompletion === "signed_up"
        ) {
            return;
        }

        posthog.capture("signed_up", {
            ...authCompletionProperties(input),
            userCreatedAt:
                createdAt === null ? null : new Date(createdAt).toISOString(),
        });
        writeLocalValue(signupKey, "true");
        writeSessionValue(sessionKey, "signed_up");
        return;
    }

    if (sessionCompletion !== null) {
        return;
    }

    posthog.capture("signed_in", authCompletionProperties(input));
    writeSessionValue(sessionKey, "signed_in");
}

export type ViewerAuthAnalyticsSnapshot = {
    authUserId: string;
    isAuthenticated: boolean;
    shouldCaptureCompleted: boolean;
    stateKey: string;
    wagsUserId: string;
};

export function viewerAuthAnalyticsSnapshot(input: {
    authState?: {
        appUserId: string;
        authUserId?: string;
        isAuthenticated: boolean;
    } | null;
    isLoaded: boolean;
    isSignedIn: boolean;
    isUserLoaded: boolean;
    userId: string | null;
}): ViewerAuthAnalyticsSnapshot | null {
    if (!input.isLoaded) return null;
    if (input.isSignedIn && input.userId && !input.isUserLoaded) return null;
    if (!input.authState) return null;

    const authUserId = input.userId ?? input.authState.authUserId;
    const wagsUserId = input.authState.appUserId;
    const isAuthenticated = input.authState.isAuthenticated && input.isSignedIn;
    if (isAuthenticated && !authUserId) return null;

    return {
        authUserId: authUserId ?? wagsUserId,
        isAuthenticated,
        shouldCaptureCompleted: Boolean(input.isSignedIn && input.userId),
        stateKey: isAuthenticated
            ? `signed-in:${authUserId}:${wagsUserId}`
            : `signed-out:${wagsUserId}`,
        wagsUserId,
    };
}

export type PostHogViewerIdentityClient = {
    identify: (
        id: string,
        properties?: {
            auth_user_id?: string;
            is_authenticated: boolean;
            wags_user_id: string;
        },
    ) => void;
    register: (properties: {
        auth_user_id?: string;
        is_authenticated: boolean;
        wags_user_id: string;
    }) => void;
    reset: () => void;
    unregister: (key: string) => void;
};

export type PostHogViewerIdentityHookClient = PostHogViewerIdentityClient &
    Pick<PostHogInterface, "capture"> &
    Partial<Pick<PostHogInterface, "get_distinct_id" | "get_property">>;

export function captureInitialSiteVisitorIdentityLink(
    posthog: PostHogViewerIdentityHookClient,
    snapshot: ViewerAuthAnalyticsSnapshot,
    discoveredAt: string,
) {
    if (posthog.get_property?.("$user_state") !== "anonymous") return;
    const previousProfileId = posthog.get_property?.("wags_user_id");
    if (typeof previousProfileId === "string" && previousProfileId.length > 0)
        return;
    const browserDistinctId = posthog.get_distinct_id?.();
    if (!browserDistinctId || browserDistinctId === snapshot.wagsUserId) return;
    posthog.capture("site_visitor_identity_linked", {
        browser_distinct_id: browserDistinctId,
        wags_user_id: snapshot.wagsUserId,
        identity_discovered_at: discoveredAt,
        identity_link_basis: "initial_anonymous",
    });
}

export function applyPostHogViewerIdentity(
    posthog: PostHogViewerIdentityClient,
    snapshot: ViewerAuthAnalyticsSnapshot,
    options?: { reset?: boolean },
) {
    if (options?.reset) {
        posthog.reset();
    }

    if (snapshot.isAuthenticated) {
        posthog.identify(snapshot.wagsUserId, {
            auth_user_id: snapshot.authUserId,
            is_authenticated: true,
            wags_user_id: snapshot.wagsUserId,
        });
        posthog.register({
            auth_user_id: snapshot.authUserId,
            is_authenticated: true,
            wags_user_id: snapshot.wagsUserId,
        });
        return;
    }

    posthog.identify(snapshot.wagsUserId, {
        is_authenticated: false,
        wags_user_id: snapshot.wagsUserId,
    });
    posthog.unregister("auth_user_id");
    posthog.register({
        is_authenticated: false,
        wags_user_id: snapshot.wagsUserId,
    });
}

export type PostHogViewerIdentityWrite =
    | { kind: "skip" }
    | {
          kind: "write";
          nextKey: string;
          reset: boolean;
          snapshot: ViewerAuthAnalyticsSnapshot;
      };

export function planPostHogViewerIdentityWrite(
    previousKey: string | null,
    snapshot: ViewerAuthAnalyticsSnapshot | null,
): PostHogViewerIdentityWrite {
    if (!snapshot) return { kind: "skip" };
    if (previousKey === snapshot.stateKey) return { kind: "skip" };
    return {
        kind: "write",
        nextKey: snapshot.stateKey,
        reset: previousKey !== null && !snapshot.isAuthenticated,
        snapshot,
    };
}

export function usePostHogViewerIdentity(
    posthog: PostHogViewerIdentityHookClient,
    snapshot: ViewerAuthAnalyticsSnapshot | null,
    userCreatedAt?: Date | number | string | null,
): void {
    const previousKey = useRef<string | null>(null);
    const sync = useEffectEvent(() => {
        const plan = planPostHogViewerIdentityWrite(
            previousKey.current,
            snapshot,
        );
        switch (plan.kind) {
            case "skip":
                return;
            case "write": {
                const initial = previousKey.current === null && !plan.reset;
                previousKey.current = plan.nextKey;
                runWhenPostHogReady(posthog, (ready) => {
                    if (initial) {
                        captureInitialSiteVisitorIdentityLink(
                            ready,
                            plan.snapshot,
                            new Date().toISOString(),
                        );
                    }
                    applyPostHogViewerIdentity(ready, plan.snapshot, {
                        reset: plan.reset,
                    });
                    if (plan.snapshot.shouldCaptureCompleted) {
                        captureAuthCompleted(ready, {
                            userCreatedAt,
                            userId: plan.snapshot.authUserId,
                        });
                    }
                });
                return;
            }
            default: {
                const _exhaustive: never = plan;
                return _exhaustive;
            }
        }
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- retrigger when the viewer identity key changes; sync is an effect event so the analyzer would collapse this to mount-only
    useUnsafeEffect(() => {
        sync();
    }, [snapshot?.stateKey]);
}

export function AuthAnalytics() {
    const { isLoaded, isSignedIn, userId } = useAuth();
    const { isLoaded: isUserLoaded, user } = useUser();
    const { data: authState } = useQuery({
        ...authQueryOptions(),
        enabled: isLoaded,
    });
    const posthog = usePostHog();

    usePostHogViewerIdentity(
        posthog,
        viewerAuthAnalyticsSnapshot({
            authState,
            isLoaded,
            isSignedIn,
            isUserLoaded,
            userId,
        }),
        user?.createdAt,
    );

    return null;
}
